import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Banknote, CheckCircle2, CircleDot, Circle, CreditCard, Gift, Loader2, Lock, ShoppingBag, Zap } from "lucide-react";
import { toast } from "sonner";
import { toOrderItems, useCart } from "@/context/CartContext";
import { useShop } from "@/context/ShopContext";
import { useAuth } from "@/context/AuthContext";
import { AppButton, Card, EmptyState, Field, SectionTitle, Stepper } from "@/components/AppUI";
import { getTable } from "@/components/TableBanner";
import { LOGO } from "@/pages/MenuPage";
import { euro, fileUrl, formatError, isPromoToday, lineMeta, papi, saveOrderId } from "@/lib/pizzeria";

const muted = { color: "var(--muted-c)" };

const SlotChip = ({ s, on, onClick }) => (
  <button type="button" onClick={onClick} data-testid={`slot-chip-${s.value}`} className="h-9 px-4 rounded-full text-sm font-medium shrink-0 whitespace-nowrap"
    style={{ ...(on ? { background: "var(--brand)", color: "var(--on-brand)", border: "1px solid var(--brand)" } : { background: "var(--surface-2)", border: "1px solid var(--border-c)" }), opacity: s.full && !on ? 0.45 : 1 }}>
    <span style={s.full ? { textDecoration: "line-through" } : {}}>{s.label}</span>{s.full && " · pieno"}
  </button>
);

export const Slots = ({ slots, value, onChange, retry, asap }) => {
  if (slots === null) return <Loader2 className="h-5 w-5 animate-spin" style={{ color: "var(--brand)" }} />;
  if (!slots.length) return <AppButton variant="outline" title="Riprova a caricare gli orari" onClick={retry} testId="slots-retry-button" />;
  const days = [...new Set(slots.map((s) => s.day))];
  const sel = slots.find((s) => s.value === value);
  const firstFree = slots.find((s) => !s.full);
  const isAsap = asap && firstFree && value === firstFree.value;
  const alts = (sel?.alternatives || []).map((a) => (typeof a === "string" ? slots.find((s) => s.value === a) || { value: a, label: a.slice(11, 16) } : a));
  return (
    <div className="space-y-3">
      {asap && firstFree && (
        <button type="button" onClick={() => onChange(firstFree.value)} data-testid="slot-asap-button" className="press w-full flex items-center gap-3 p-3.5 rounded-xl text-left"
          style={{ border: isAsap ? "2px solid var(--brand)" : "1px solid var(--border-c)", background: isAsap ? "var(--brand-3)" : "var(--surface-2)" }}>
          <Zap className="h-[22px] w-[22px]" style={{ color: isAsap ? "var(--brand)" : "var(--muted-c)" }} />
          <span className="flex-1"><span className="block text-[15px] font-medium">Prima possibile</span><span className="block text-xs" style={muted}>Ritiro {firstFree.day.toLowerCase()} alle {firstFree.label}, il primo orario libero</span></span>
          {isAsap ? <CircleDot className="h-5 w-5" style={{ color: "var(--brand)" }} /> : <Circle className="h-5 w-5" style={muted} />}
        </button>
      )}
      {asap && firstFree && <p className="text-xs font-medium uppercase" style={muted}>Oppure scegli un orario</p>}
      {days.map((d) => (
        <div key={d}>
          <p className="text-xs font-medium uppercase mb-1" style={muted}>{d}</p>
          <div className="-mx-4 px-4 h-14 flex items-center gap-2 overflow-x-auto no-scrollbar">
            {slots.filter((s) => s.day === d).map((s) => <SlotChip key={s.value} s={s} on={value === s.value} onClick={() => onChange(s.value)} />)}
          </div>
        </div>
      ))}
      {sel?.full && (
        <div className="rounded-xl p-3 space-y-2" style={{ background: "var(--brand-3)", color: "var(--on-brand-3)" }} data-testid="slot-full-box">
          <p className="text-sm font-medium">Alle {sel.label} il forno è pieno.</p>
          <p className="text-sm">Scegli un altro orario o un altro giorno.</p>
          {alts.length > 0 && <><p className="text-sm">Orari più vicini disponibili:</p><div className="flex flex-wrap gap-2">{alts.map((a) => <button key={a.value} type="button" onClick={() => onChange(a.value)} className="h-9 px-4 rounded-full text-sm font-medium" style={{ background: "var(--surface-2)", color: "var(--on-surface)" }} data-testid={`slot-alt-${a.value}`}>{a.label}</button>)}</div></>}
        </div>
      )}
    </div>
  );
};

const CartItems = ({ items, setQty }) => (
  <Card className="px-3">
    {items.map((x, i) => (
      <div key={x.key} className="flex items-center gap-3 py-3" style={i ? { borderTop: "1px solid var(--border-c)" } : {}} data-testid={`cart-item-${x.key}`}>
        <img src={x.image ? fileUrl(x.image) : LOGO} alt={x.name} className="h-14 w-14 rounded-xl object-cover shrink-0" style={{ background: "var(--surface-3)" }} />
        <div className="flex-1 min-w-0 space-y-0.5">
          <p className="text-base font-medium">{x.name}</p>
          <p className="text-xs" style={muted}>{lineMeta(x)}</p>
          <p className="text-sm font-medium">{euro(x.unit_price * x.quantity)}</p>
        </div>
        <Stepper value={x.quantity} onChange={(q) => setQty(x.key, q)} testIdPrefix={`cart-qty-${i}`} trash />
      </div>
    ))}
  </Card>
);

const PayOpt = ({ on, onClick, icon: Icon, title, sub, id }) => (
  <button type="button" onClick={onClick} data-testid={`pay-method-${id}`} className="w-full flex items-center gap-3 p-3.5 min-h-[56px] rounded-xl text-left"
    style={{ border: on ? "2px solid var(--brand)" : "1px solid var(--border-c)", background: on ? "var(--brand-3)" : "var(--surface-2)" }}>
    <Icon className="h-[22px] w-[22px]" style={{ color: on ? "var(--brand)" : "var(--muted-c)" }} />
    <span className="flex-1"><span className="block text-[15px] font-medium">{title}</span><span className="block text-xs" style={muted}>{sub}</span></span>
    {id === "online" && <Lock className="h-4 w-4" style={muted} />}
    {on ? <CircleDot className="h-5 w-5" style={{ color: "var(--brand)" }} /> : <Circle className="h-5 w-5" style={muted} />}
  </button>
);

const LoyaltyNote = ({ user, hasPizza }) => {
  const [l, setL] = useState(null);
  useEffect(() => {
    if (user) {
      papi.get("/loyalty").then(({ data }) => setL(data)).catch(() => {});
    }
  }, [user]);
  const text = !user ? "Registrati per la carta fedeltà: ogni 10 ordini una pizza in omaggio"
    : !l ? null
    : l.available > 0 ? (hasPizza ? "Premio fedeltà applicato: una pizza in omaggio" : "Hai una pizza in omaggio: aggiungi una pizza per usarla")
    : `Carta fedeltà: ${l.progress || 0}/${l.target || 10} ordini. Al 10° la pizza è gratis!`;
  if (!text) return null;
  return <div className="flex items-center gap-3 p-4 rounded-xl" style={{ background: "var(--brand-3)", color: "var(--on-brand-3)" }} data-testid="cart-loyalty-note"><Gift className="h-[22px] w-[22px] shrink-0" /><span className="text-sm font-medium">{text}</span></div>;
};

const Totals = ({ quote, total }) => {
  const { promos } = useShop();
  const sub = quote?.subtotal ?? total;
  const next = promos.filter((pr) => pr.kind === "order" && pr.min_total > sub && isPromoToday(pr)).sort((a, b) => a.min_total - b.min_total)[0];
  return (
    <div className="space-y-1" data-testid="cart-totals">
      {quote?.discount > 0 && <div className="flex justify-between text-sm" style={muted}><span>Subtotale</span><span data-testid="cart-subtotal">{euro(sub)}</span></div>}
      {(quote?.promos || []).map((pr, i) => (
        <div key={i} className="flex justify-between text-sm font-medium" style={{ color: "var(--success)" }} data-testid={`cart-promo-${i}`}><span>{pr.name || "Sconto"}{pr.percent ? ` -${pr.percent}%` : ""}</span><span>{pr.amount != null || pr.discount != null ? `-${euro(pr.amount ?? pr.discount)}` : ""}</span></div>
      ))}
      {quote?.discount > 0 && !quote?.promos?.length && <div className="flex justify-between text-sm font-medium" style={{ color: "var(--success)" }}><span>Sconto</span><span>-{euro(quote.discount)}</span></div>}
      {next && <p className="text-xs font-medium" style={{ color: "var(--brand)" }} data-testid="cart-promo-hint">Aggiungi {euro(next.min_total - sub)} per lo sconto {next.name} (-{next.percent}%)</p>}
      <div className="flex justify-between items-center"><span className="text-base">Totale</span><span className="text-xl font-medium" data-testid="cart-total">{euro(quote?.total ?? total)}</span></div>
    </div>
  );
};

export default function Cart() {
  const { items, setQty, clear, total, teglie, rotonde } = useCart();
  const { settings, payments } = useShop();
  const { user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ customer_name: "", phone: "", notes: "" });
  const [slots, setSlots] = useState(null);
  const [slot, setSlot] = useState("");
  const [pay, setPay] = useState("ritiro");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [quote, setQuote] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const loadSlots = useMemo(() => () => papi.get("/slots", { params: { teglie, rotonde } }).then(({ data }) => setSlots(data)).catch(() => setSlots([])), [teglie, rotonde]);
  useEffect(() => {
    if (items.length) {
      loadSlots();
      const t = setInterval(loadSlots, 60000);
      return () => clearInterval(t);
    }
  }, [loadSlots, items.length]);
  useEffect(() => {
    if (!items.length) return undefined;
    const t = setTimeout(() => {
      papi.post("/orders/quote", { items: toOrderItems(items) }).then(({ data }) => setQuote(data)).catch(() => setQuote(null));
    }, 350);
    return () => clearTimeout(t);
  }, [items]);
  useEffect(() => {
    if (user) {
      setForm((f) => ({ ...f, customer_name: f.customer_name || user.name || "", phone: f.phone || user.phone || "" }));
    }
  }, [user]);

  if (!items.length) {
    return (
      <div className="flex-1">
        <h1 className="text-2xl font-medium px-4 pt-2 pb-3">Carrello</h1>
        <EmptyState icon={ShoppingBag} title="Il tuo carrello è vuoto" subtitle="Scegli le tue pizze preferite dal menù." testId="cart-empty"><AppButton title="Inizia a ordinare" onClick={() => nav("/")} testId="cart-start-ordering-button" /></EmptyState>
      </div>
    );
  }

  const paused = settings?.orders_paused;
  const selected = slots?.find((s) => s.value === slot);
  const submit = async () => {
    const name = form.customer_name.trim();
    const phone = form.phone.trim();
    if (!slot) return setError("Seleziona un orario di ritiro");
    if (selected?.full) return setError(`Alle ${selected.label} il forno è pieno. Scegli un altro orario.`);
    if (!name) return setError("Inserisci il tuo nome");
    if (phone.replace(/\D/g, "").length < 6) return setError("Inserisci un numero di telefono valido");
    setError("");
    setSending(true);
    try {
      const table = getTable();
      const notes = [table && `Tavolo ${table}`, form.notes.trim()].filter(Boolean).join(" · ");
      const { data } = await papi.post("/orders", {
        customer_name: name, phone, pickup_time: slot, notes, payment_method: pay,
        items: toOrderItems(items),
      });
      const id = data.id || data._id;
      saveOrderId(id);
      if (pay === "online") {
        try {
          const { data: s } = await papi.post(`/orders/${id}/checkout-session`, { origin: window.location.origin });
          if (s.url || s.checkout_url) {
            clear();
            window.location.href = s.url || s.checkout_url;
            return;
          }
        } catch (err) {
          toast.error(`Ordine #${data.code} creato, ma il pagamento non si è avviato: ${formatError(err)}`);
        }
        clear();
        nav(`/order/${id}`);
        return;
      }
      clear();
      toast.success(`Ordine #${data.code} inviato!`);
      nav(`/order/${id}`);
    } catch (err) {
      setError(formatError(err));
      loadSlots();
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col" data-testid="cart-page">
      <h1 className="text-2xl font-medium px-4 pt-2 pb-3">Carrello</h1>
      <div className="flex-1 px-4 pb-6 space-y-6">
        <CartItems items={items} setQty={setQty} />
        <div><SectionTitle>Orario di ritiro</SectionTitle><Slots slots={slots} value={slot} asap={settings?.asap_enabled} onChange={(v) => { setSlot(v); setError(""); }} retry={loadSlots} /></div>
        {paused && <p className="rounded-xl p-3 text-sm font-medium" style={{ background: "var(--brand-3)", color: "var(--on-brand-3)" }} data-testid="cart-orders-paused">Al momento la pizzeria non accetta ordini dall'app. Chiamaci per ordinare.</p>}
        <div className="space-y-3">
          <SectionTitle>I tuoi dati</SectionTitle>
          <Field label="Nome" testId="checkout-name-input" value={form.customer_name} onChange={set("customer_name")} placeholder="Mario Rossi" autoComplete="name" />
          <Field label="Telefono" testId="checkout-phone-input" value={form.phone} onChange={set("phone")} placeholder="333 1234567" type="tel" />
          <Field label="Note (facoltative)" testId="checkout-notes-input" value={form.notes} onChange={set("notes")} placeholder="Es. tagliata a spicchi, senza basilico..." multiline />
          {!user && <Link to="/profilo" className="block text-sm font-medium" style={{ color: "var(--brand)" }} data-testid="checkout-login-link">Hai un account? Accedi per ritrovare i tuoi ordini</Link>}
        </div>
        <div className="space-y-2">
          <SectionTitle>Pagamento</SectionTitle>
          {payments?.enabled && <PayOpt id="online" on={pay === "online"} onClick={() => setPay("online")} icon={CreditCard} title="Paga ora online" sub="Carta, Apple Pay o Google Pay: niente fila alla cassa" />}
          <PayOpt id="ritiro" on={pay === "ritiro"} onClick={() => setPay("ritiro")} icon={Banknote} title="Paga al ritiro" sub="Contanti o carta in pizzeria" />
        </div>
        <LoyaltyNote user={user} hasPizza={items.some((x) => x.size)} />
      </div>
      <div className="sticky bottom-16 z-30 px-4 pt-3 pb-3 space-y-2" style={{ background: "var(--surface)", borderTop: "1px solid var(--border-c)" }}>
        {error && <p className="text-sm" style={{ color: "var(--brand)" }} data-testid="checkout-error">{error}</p>}
        <Totals quote={quote} total={total} />
        <AppButton title={pay === "online" ? "Vai al pagamento" : "Conferma ordine"} icon={pay === "online" ? CreditCard : CheckCircle2} loading={sending} disabled={paused} onClick={submit} testId="checkout-submit-button" className="w-full" />
      </div>
    </div>
  );
}
